import React, { useState, useEffect, useCallback, useRef } from "react";
import { getVerificationTickets, resolveVerification } from "../api";

export default function AgronomistDashboard({ sessionId, onLogout }) {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("Pending");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const profileMenuRef = useRef(null);

  // Pagination states
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalPending, setTotalPending] = useState(0);
  const [totalResolved, setTotalResolved] = useState(0);
  const limit = 30;

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);

  const [correctedCrop, setCorrectedCrop] = useState("");
  const [correctedDisease, setCorrectedDisease] = useState("None");
  const [correctedPest, setCorrectedPest] = useState("None");
  const [correctedStage, setCorrectedStage] = useState("None");
  const [correctedCategory, setCorrectedCategory] = useState("None");
  const [correctedRecommendations, setCorrectedRecommendations] = useState("");

  const cropsList = ["Paddy", "Wheat", "Maize", "Cotton", "Chickpea", "Soybean", "Mustard", "undefined"];
  const categoriesList = ["Disease", "Pest", "Disease & Pest", "Healthy", "None"];
  const stagesList = ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity", "None"];

  const CROP_DISEASE_MAP = {
    Paddy: ["Bacterial Blight", "Blast", "Brown Spot", "Tungro", "Healthy", "None"],
    Wheat: ["Flag Smut", "Fusarium Foot Rot", "Leaf Blight", "Powdery Mildew", "Rust", "Healthy", "None"],
    Maize: ["Ear Rot", "Leaf Blight", "Rust", "Leaf Spot", "Streak Virus", "Healthy", "None"],
    Cotton: ["Anthracnose", "Bacterial Blight", "Leaf Curl", "Powdery Mildew", "Target Spot", "Healthy", "None"],
    Chickpea: ["Ascochyta Blight", "Botrytis Gray Mold", "Dry Root Rot", "Fusarium Wilt", "Healthy", "None"],
    Soybean: ["Bacterial Blight", "Cercospora Leaf Blight", "Downy Mildew", "Frogeye Spot", "Rust", "Target Spot", "Healthy", "None"],
    Mustard: ["Alternaria Blight", "White Rust", "Downy Mildew", "Powdery Mildew", "Sclerotinia Rot", "Healthy", "None"],
    undefined: ["None"]
  };
  const CROP_PEST_MAP = {
    Paddy: ["Armyworm", "Rice Stem Borer", "Brown Planthopper", "Rice Gall Midge", "Green Leafhopper", "Rice Gundhi Bug", "Rice Hispa", "None"],
    Wheat: ["Aphids", "Wheat Stem Sawfly", "Hessian Fly", "None"],
    Maize: ["Fall Armyworm", "Corn Earworm", "Aphids", "None"],
    Cotton: ["Bollworm", "Whitefly", "Aphids", "Thrips", "None"],
    Chickpea: ["Pod Borer", "Aphids", "Cut Worm", "None"],
    Soybean: ["Bean Pod Borer", "Soybean Looper", "Aphids", "None"],
    Mustard: ["Mustard Aphid", "Painted Bug", "Sawfly", "None"],
    undefined: ["None"]
  };
  const CROP_STAGE_MAP = {
    Paddy: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity", "None"],
    Wheat: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity", "None"],
    Maize: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity", "None"],
    Cotton: ["Seedling", "Vegetative Growth", "Flowering Squaring", "Boll Development", "Boll Bursting Maturity", "None"],
    Chickpea: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity", "None"],
    Soybean: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Pod Development", "Maturity", "None"],
    Mustard: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Pod Development", "Maturity", "None"],
    undefined: ["None"]
  };

  const diseasesForCrop = CROP_DISEASE_MAP[correctedCrop] || ["None"];
  const pestsForCrop = CROP_PEST_MAP[correctedCrop] || ["None"];
  const stagesForCrop = CROP_STAGE_MAP[correctedCrop] || stagesList;

  const allPestsList = Array.from(new Set(Object.values(CROP_PEST_MAP).flat())).sort((a, b) => {
    if (a === "None") return 1;
    if (b === "None") return -1;
    return a.localeCompare(b);
  });

  // Initialize and load first batch of tickets
  const loadInitialTickets = useCallback(async (currentFilter = filter) => {
    setLoading(true);
    setError("");
    try {
      const data = await getVerificationTickets(sessionId, limit, 0, currentFilter);
      if (data.status === "success") {
        setTickets(data.tickets || []);
        setTotalPending(data.total_pending || 0);
        setTotalResolved(data.total_resolved || 0);
        setOffset(limit);
        setHasMore((data.tickets || []).length === limit);
      } else {
        setError("Failed to fetch verification tickets.");
      }
    } catch (err) {
      console.error(err);
      setError("Unable to connect to the server to load tickets.");
    } finally {
      setLoading(false);
    }
  }, [sessionId, filter]);

  // Load next batch on scroll
  const loadMoreTickets = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const data = await getVerificationTickets(sessionId, limit, offset, filter);
      if (data.status === "success") {
        const newTickets = data.tickets || [];
        setTickets((prev) => [...prev, ...newTickets]);
        setTotalPending(data.total_pending || 0);
        setTotalResolved(data.total_resolved || 0);
        setOffset((prev) => prev + limit);
        setHasMore(newTickets.length === limit);
      }
    } catch (err) {
      console.error("Failed to load more tickets", err);
    } finally {
      setLoading(false);
    }
  }, [sessionId, filter, offset, loading, hasMore]);

  // Trigger loading when tab filter changes
  useEffect(() => {
    loadInitialTickets(filter);
  }, [filter, loadInitialTickets]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreTickets();
    }
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setSuccess("");
    setError("");
    const orig = ticket.original_prediction || {};
    setCorrectedCrop(orig.crop?.label || orig.crop || "undefined");
    setCorrectedDisease(orig.pest?.disease || orig.disease || "None");
    setCorrectedPest(orig.pest?.pest || orig.pest || "None");
    setCorrectedStage(orig.stage?.label || orig.stage || "None");
    setCorrectedCategory(orig.pest?.category || orig.category || "None");
    const val = ticket.validated_prediction || {};
    setCorrectedRecommendations(val.recommendations || "");
  };

  const handleCloseModal = () => { setSelectedTicket(null); setError(""); };

  const handleSubmitValidation = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const validated = { crop: correctedCrop, disease: correctedDisease, pest: correctedPest, stage: correctedStage, category: correctedCategory, recommendations: correctedRecommendations };
    try {
      const data = await resolveVerification(sessionId, selectedTicket.ticket_id, validated);
      if (data.status === "success") {
        setSuccess(`Ticket ${selectedTicket.ticket_id} resolved successfully!`);
        // Remove ticket from Pending list or update in Resolved list locally
        if (filter === "Pending") {
          setTickets((prev) => prev.filter((t) => t.ticket_id !== selectedTicket.ticket_id));
          setTotalPending((prev) => Math.max(0, prev - 1));
          setTotalResolved((prev) => prev + 1);
        } else {
          setTickets((prev) => prev.map((t) => t.ticket_id === selectedTicket.ticket_id ? { ...t, status: "Resolved", validated_prediction: validated } : t));
        }
        setTimeout(() => { setSelectedTicket(null); setSuccess(""); }, 1200);
      } else {
        setError("Failed to submit corrections.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to connect to the backend.");
    } finally {
      setSubmitting(false);
    }
  };

  const getImageSrc = (img) => {
    if (!img) return null;
    if (img.startsWith("data:") || img.startsWith("http") || img.startsWith("/")) return img;
    return `data:image/jpeg;base64,${img}`;
  };

  const SelectField = ({ label, value, onChange, options, predicted }) => (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] text-on-surface-variant font-bold uppercase tracking-wider flex items-center gap-1 flex-wrap">
        {label}
        {predicted && <span className="text-[9px] normal-case text-primary/60 font-normal">(predicted: {predicted})</span>}
      </label>
      <select value={value} onChange={onChange} className="py-2 px-3 bg-surface-container border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary font-medium">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  const PestSelectField = ({ label, value, onChange, options, predicted }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleOutsideClick = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    return (
      <div className="flex flex-col gap-1 relative" ref={dropdownRef}>
        <label className="text-[10px] text-on-surface-variant font-bold uppercase tracking-wider flex items-center gap-1 flex-wrap">
          {label}
          {predicted && <span className="text-[9px] normal-case text-primary/60 font-normal">(predicted: {predicted})</span>}
        </label>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex justify-between items-center py-2 px-3 bg-surface-container border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary font-medium text-left w-full cursor-pointer"
        >
          <span>{value}</span>
          <span className="material-symbols-outlined text-[16px] transition-transform duration-200" style={{ transform: isOpen ? "rotate(180deg)" : "none" }}>
            expand_more
          </span>
        </button>
        {isOpen && (
          <div className="absolute top-[100%] left-0 right-0 mt-1 max-h-40 overflow-y-auto bg-surface-container border border-outline-variant rounded-xl shadow-lg z-50 py-1">
            {options.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => {
                  onChange({ target: { value: o } });
                  setIsOpen(false);
                }}
                className={`w-full text-left py-2 px-3 hover:bg-surface-container-highest text-sm transition-colors cursor-pointer ${o === value ? "bg-primary/10 text-primary font-semibold" : "text-on-surface"}`}
              >
                {o}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Detect which module raised the flag to conditionally show validation fields
  const ticketSource = selectedTicket?.original_prediction?.source_module || "report";

  return (
    <div className="min-h-screen w-screen bg-background text-on-background flex flex-col font-body-lg overflow-hidden">
      <header className="bg-surface border-b border-outline-variant/30 h-16 shrink-0 px-6 flex items-center justify-between z-30 shadow-sm gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <div className="bg-white/95 p-1 rounded border border-outline-variant/30 flex items-center justify-center h-10 w-24">
            <img src={process.env.PUBLIC_URL + "/niruthi_logo.png"} alt="Niruthi Logo" className="h-full object-contain" />
          </div>
          <span className="text-outline-variant">|</span>
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-extrabold tracking-wide uppercase select-none hidden sm:block">Agronomist Dashboard</h2>
        </div>

        {/* Tab Switches (full counts shown in badges) */}
        <div className="flex items-center gap-1 bg-surface-container rounded-xl p-1 border border-outline-variant/20">
          <button onClick={() => setFilter("Pending")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs uppercase tracking-wider transition-all duration-200 ${filter === "Pending" ? "bg-primary text-on-primary shadow-sm" : "text-on-surface-variant hover:bg-surface-container-highest"}`}>
            <span className="material-symbols-outlined text-[13px]">schedule</span>
            Pending
            {totalPending > 0 && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-extrabold flex items-center justify-center ${filter === "Pending" ? "bg-white/25 text-on-primary" : "bg-primary text-on-primary"}`}>{totalPending}</span>}
          </button>
          <button onClick={() => setFilter("Resolved")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs uppercase tracking-wider transition-all duration-200 ${filter === "Resolved" ? "bg-secondary text-on-secondary shadow-sm" : "text-on-surface-variant hover:bg-surface-container-highest"}`}>
            <span className="material-symbols-outlined text-[13px]">check_circle</span>
            Completed
            {totalResolved > 0 && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-extrabold flex items-center justify-center ${filter === "Resolved" ? "bg-white/25 text-on-secondary" : "bg-secondary text-on-secondary"}`}>{totalResolved}</span>}
          </button>
        </div>

        {/* Refresh + User profile menu side-by-side */}
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={() => loadInitialTickets(filter)} disabled={loading} className="flex items-center gap-1.5 text-xs font-bold text-on-surface-variant hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-surface-container">
            <span className={`material-symbols-outlined text-[16px] ${loading ? "animate-spin" : ""}`}>refresh</span>
            Refresh
          </button>

          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-outline-variant/30 overflow-hidden bg-primary/10 hover:border-primary transition-all duration-250 shadow-sm hover:scale-105"
              title="Agronomist"
            >
              <span className="font-bold text-sm text-primary">A</span>
            </button>
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-surface-container-high border border-outline-variant/40 rounded-xl shadow-lg py-2 z-50">
                <div className="px-4 py-2 border-b border-outline-variant/20 mb-1">
                  <p className="font-bold text-sm text-on-surface">Agronomist</p>
                  <p className="text-xs text-on-surface-variant">Expert Reviewer</p>
                </div>
                <button onClick={onLogout} className="w-full text-left px-4 py-2 hover:bg-error-container/20 text-error flex items-center gap-2 transition-colors font-semibold">
                  <span className="material-symbols-outlined text-[18px]">logout</span> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6" onScroll={handleScroll}>
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg text-on-surface">{filter === "Pending" ? "Awaiting Your Review" : "Completed Verifications"}</h3>
            <p className="text-xs text-on-surface-variant mt-0.5">{filter === "Pending" ? "Click a scan card to open the validation workspace." : "These tickets have been resolved with expert annotations."}</p>
          </div>
        </div>

        {error && !selectedTicket && <div className="mb-4 p-3 bg-error-container/20 border border-error/30 rounded-xl text-error text-sm font-semibold flex items-center gap-2"><span className="material-symbols-outlined text-[16px]">error</span>{error}</div>}
        {success && !selectedTicket && <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-xl text-primary text-sm font-semibold flex items-center gap-2"><span className="material-symbols-outlined text-[16px]">check_circle</span>{success}</div>}

        {loading && tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="text-sm text-on-surface-variant font-semibold">Loading tickets...</span>
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center text-on-surface-variant">
            <span className="material-symbols-outlined text-[56px] opacity-25 mb-3">{filter === "Pending" ? "pending_actions" : "task_alt"}</span>
            <p className="font-bold text-base">No {filter.toLowerCase()} tickets</p>
            <p className="text-xs mt-1 opacity-60">{filter === "Pending" ? "All requests reviewed." : "No completed verifications yet."}</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {tickets.map((ticket) => {
                const orig = ticket.original_prediction || {};
                const val = ticket.validated_prediction || {};
                const cropLabel = orig.crop?.label || orig.crop || "undefined";
                const diseaseLabel = orig.pest?.disease || orig.disease || "None";
                const imageSrc = getImageSrc(ticket.image);
                const isResolved = ticket.status === "Resolved";
                return (
                  <div key={ticket.ticket_id} onClick={() => handleSelectTicket(ticket)} className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.03] border border-outline-variant/20 bg-surface-container flex flex-col" style={{ aspectRatio: "3/4" }}>
                    <div className="flex-1 overflow-hidden bg-black/20 relative min-h-0">
                      {imageSrc ? (
                        <img src={imageSrc} alt={`Scan ${ticket.ticket_id}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-surface-container-highest"><span className="material-symbols-outlined text-[36px] text-on-surface-variant/30">image_not_supported</span></div>
                      )}
                      <div className={`absolute top-2 right-2 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider flex items-center gap-0.5 ${isResolved ? "bg-secondary/90 text-on-secondary" : "bg-primary/90 text-on-primary"}`}>
                        {isResolved ? "Done" : "Review"}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-1.5">
                          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <span className="material-symbols-outlined text-[20px] text-primary">{isResolved ? "visibility" : "edit_note"}</span>
                          </div>
                          <span className="text-white text-[10px] font-bold">{isResolved ? "View" : "Validate"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-2.5 bg-surface-container shrink-0">
                      <p className="font-bold text-xs text-on-surface truncate capitalize">{cropLabel}</p>
                      <p className="text-[10px] text-on-surface-variant truncate mt-0.5">{isResolved ? (val.disease || val.category || "Verified") : (diseaseLabel !== "None" ? diseaseLabel : "Awaiting review")}</p>
                      <p className="text-[9px] text-on-surface-variant/50 font-mono mt-1 truncate">{ticket.ticket_id}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load more spinner */}
            {loading && (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200" onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}>
          <div className="bg-surface text-on-surface w-full max-w-5xl rounded-3xl shadow-2xl border border-outline-variant/30 flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-outline-variant/20 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-[20px]">edit_note</span>
                </div>
                <div>
                  <h3 className="font-bold text-base text-on-surface">Expert Validation</h3>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="font-mono text-xs bg-primary/10 text-primary py-0.5 px-2 rounded-full">{selectedTicket.ticket_id}</span>
                    <span className="text-xs text-on-surface-variant">by <span className="font-semibold">{selectedTicket.username}</span></span>
                    <span className="text-xs text-on-surface-variant">· {new Date(selectedTicket.timestamp * 1000).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              <button onClick={handleCloseModal} className="p-2 hover:bg-surface-container-high rounded-full transition-colors">
                <span className="material-symbols-outlined text-[22px] text-on-surface-variant">close</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleSubmitValidation} className="p-6 flex flex-col lg:flex-row gap-6">
                {/* Left: Image + Original */}
                <div className="flex-1 flex flex-col gap-4">
                  <div className="w-full aspect-video rounded-2xl border border-outline-variant/30 overflow-hidden bg-black flex items-center justify-center shadow-inner relative">
                    {selectedTicket.image ? (
                      <img src={selectedTicket.image.startsWith("data:") ? selectedTicket.image : `data:image/jpeg;base64,${selectedTicket.image}`} alt="Scan" className="max-h-full max-w-full object-contain" />
                    ) : (
                      <span className="material-symbols-outlined text-[48px] text-white/30">image_not_supported</span>
                    )}
                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white py-1 px-3 rounded-full text-xs font-bold">Flagged Imagery</div>
                  </div>
                  <div className="bg-surface-container-low p-4 rounded-xl border border-outline-variant/20">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant mb-3 flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[14px]">smart_toy</span>
                      Model Prediction
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: "Crop", value: selectedTicket.original_prediction?.crop?.label || selectedTicket.original_prediction?.crop || "undefined" },
                        { label: "Stage", value: selectedTicket.original_prediction?.stage?.label || selectedTicket.original_prediction?.stage || "None" },
                        { label: "Disease", value: selectedTicket.original_prediction?.pest?.disease || selectedTicket.original_prediction?.disease || "None" },
                        { label: "Pest", value: selectedTicket.original_prediction?.pest?.pest || selectedTicket.original_prediction?.pest || "None" },
                        { label: "Category", value: selectedTicket.original_prediction?.pest?.category || selectedTicket.original_prediction?.category || "None" },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <span className="text-[10px] text-on-surface-variant block uppercase font-bold tracking-wider">{label}</span>
                          <span className={`font-bold text-sm ${value === "None" || value === "undefined" ? "text-on-surface-variant/50 italic" : "text-on-surface"}`}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: Expert Correction (Conditional fields based on flagged module) */}
                <div className="w-full lg:w-[340px] shrink-0 flex flex-col gap-3">
                  <div className="bg-surface p-5 rounded-2xl border border-outline-variant/30 shadow-sm flex flex-col gap-3">
                    <h3 className="font-bold text-base text-on-surface border-b border-outline-variant/20 pb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary text-[18px]">verified</span>
                      Expert Correction
                    </h3>

                    {/* Crop Validation (Only if raised from Crop or Report) */}
                    {(ticketSource === "crop" || ticketSource === "report") && (
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-on-surface-variant font-bold uppercase tracking-wider">Correct Crop</label>
                        <select value={correctedCrop} onChange={(e) => { setCorrectedCrop(e.target.value); setCorrectedDisease("None"); setCorrectedPest("None"); setCorrectedStage("None"); }} className="py-2 px-3 bg-surface-container border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary font-medium">
                          {cropsList.map((c) => <option key={c} value={c.toLowerCase() === "undefined" ? "undefined" : c}>{c}</option>)}
                        </select>
                      </div>
                    )}

                    {/* Pest/Disease Validation (Only if raised from Pest or Report) */}
                    {(ticketSource === "pest" || ticketSource === "report") && (
                      <>
                        <SelectField label="Correct Disease" value={correctedDisease} onChange={(e) => setCorrectedDisease(e.target.value)} options={diseasesForCrop} predicted={selectedTicket?.original_prediction?.disease || "None"} />
                        <PestSelectField label="Correct Pest" value={correctedPest} onChange={(e) => setCorrectedPest(e.target.value)} options={allPestsList} predicted={selectedTicket?.original_prediction?.pest?.pest || selectedTicket?.original_prediction?.pest || "None"} />
                      </>
                    )}

                    {/* Stage Validation (Only if raised from Stage or Report) */}
                    {(ticketSource === "stage" || ticketSource === "report") && (
                      <SelectField label="Correct Stage" value={correctedStage} onChange={(e) => setCorrectedStage(e.target.value)} options={stagesForCrop} predicted={selectedTicket?.original_prediction?.stage?.label || selectedTicket?.original_prediction?.stage || "None"} />
                    )}

                    {/* Category Validation (Only if raised from Pest or Report) */}
                    {(ticketSource === "pest" || ticketSource === "report") && (
                      <SelectField label="Category" value={correctedCategory} onChange={(e) => setCorrectedCategory(e.target.value)} options={categoriesList} predicted={selectedTicket?.original_prediction?.pest?.category || selectedTicket?.original_prediction?.category || "None"} />
                    )}

                    {/* Expert Recommendations (Only if raised from Pest or Report) */}
                    {(ticketSource === "pest" || ticketSource === "report") && (
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-on-surface-variant font-bold uppercase tracking-wider">Expert Recommendations</label>
                        <textarea value={correctedRecommendations} onChange={(e) => setCorrectedRecommendations(e.target.value)} placeholder="Treatment steps, prevention measures, and agronomist notes..." rows={3} className="py-2 px-3 bg-surface-container border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary font-medium resize-none leading-relaxed" />
                      </div>
                    )}
                    {error && <p className="text-xs text-error font-semibold">{error}</p>}
                  </div>

                  {selectedTicket.status === "Pending" ? (
                    <button type="submit" disabled={submitting} className="w-full py-3 bg-primary hover:bg-primary-container text-on-primary font-bold rounded-xl shadow-md transition-all active:scale-[0.98] flex items-center justify-center gap-2">
                      {submitting ? (<><div className="w-4 h-4 border-2 border-on-primary border-t-transparent rounded-full animate-spin" /><span>Submitting...</span></>) : (<><span className="material-symbols-outlined text-[18px]">verified</span><span>Submit Correction</span></>)}
                    </button>
                  ) : (
                    <div className="w-full text-center py-3 bg-secondary/10 text-secondary font-bold text-sm rounded-xl border border-secondary/20 flex items-center justify-center gap-1.5">
                      <span className="material-symbols-outlined text-[16px]">check_circle</span> Already Resolved
                    </div>
                  )}
                  <button type="button" onClick={handleCloseModal} className="w-full py-2.5 bg-surface-container-high text-on-surface hover:bg-surface-container-highest font-bold rounded-xl transition-all text-sm">Close</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
